import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { toCamelCase, toSnakeCase } from '@/lib/case';

import { useAuthAxios } from '@/hooks/useAuthAxios';

import {
    AttachmentUploadRequest,
    CreateFeedbackArgs,
    CreateLegacyFeedbackArgs,
    DBFeedback,
    PresignedAttachment,
} from './types';

const FEEDBACK_KEY = ['feedback'] as const;

/** The current user's own feedback, newest first — nothing is persisted
 * locally, this reads straight from the license server. */
export const useFeedback = () => {
    const { client, enabled } = useAuthAxios();

    return useQuery({
        queryKey: FEEDBACK_KEY,
        queryFn: async () => {
            const res = await client.get('/api/feedback/');
            return toCamelCase<DBFeedback[]>(res.data);
        },
        enabled,
    });
};

/** Submit the structured feedback form, returning the created row. */
export const useCreateFeedback = () => {
    const { client } = useAuthAxios();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (args: CreateFeedbackArgs) => {
            const res = await client.post('/api/feedback/form', toSnakeCase(args));
            return toCamelCase<DBFeedback>(res.data);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: FEEDBACK_KEY });
        },
    });
};

/** Submit title-and-description feedback (the flow shown while the form flag is off). */
export const useCreateLegacyFeedback = () => {
    const { client } = useAuthAxios();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({
            title,
            description,
            attachments,
        }: CreateLegacyFeedbackArgs) => {
            const res = await client.post(
                '/api/feedback/',
                toSnakeCase({ title, description, attachments }),
            );
            return toCamelCase<DBFeedback>(res.data);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: FEEDBACK_KEY });
        },
    });
};

/** Request a pre-signed S3 PUT URL per file, so attachment bytes upload
 * straight to S3 — never through our own API. */
export const usePresignAttachments = () => {
    const { client } = useAuthAxios();

    return useMutation({
        mutationFn: async (files: AttachmentUploadRequest[]) => {
            const res = await client.post(
                '/api/feedback/attachments/presign',
                toSnakeCase(files),
            );
            return toCamelCase<PresignedAttachment[]>(res.data);
        },
    });
};
